import { addItemToCart } from '@/js/add-to-cart';

/** @type {string} */
const PACK_RADIO_SELECTOR = '[data-pack-radio]';

/**
 * Variant id from the same product form
 * @param {HTMLFormElement} form
 * @returns {string | undefined}
 */
function getVariantIdFromProductForm(form) {
  return form.querySelector('input[data-variant-id]')?.value || undefined;
}

/**
 * Bulk pack selection (radios) plus a single Add to cart CTA for the selected pack size
 * @extends HTMLElement
 */
class ProductMultiAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.button = this.querySelector('[data-product-multi-add-cta]');
    if (!this.button) {
      // eslint-disable-next-line no-console
      console.error(
        'ProductMultiAdd: Missing bulk Add to cart button. Add [data-product-multi-add-cta] inside product-multi-add.'
      );
      return;
    }
    this.bindEventListeners();
    this.bindPackSummaryListeners();
    this.syncPackSummary();
  }

  /**
   * Updates the summary region from the checked pack radio's data attributes
   * @returns {void}
   */
  syncPackSummary() {
    const radio = this.querySelector(`${PACK_RADIO_SELECTOR}:checked`);
    const heading = this.querySelector('[data-pack-summary-heading]');
    const badge = this.querySelector('[data-pack-summary-badge]');
    const priceAmount = this.querySelector('[data-pack-summary-price-amount]');
    if (!radio || !heading || !badge || !priceAmount) {
      return;
    }

    heading.textContent = radio.dataset.packHeading ?? '';
    badge.textContent = radio.dataset.packDiscountBadge ?? '';
    priceAmount.textContent = radio.dataset.packPriceAmount ?? '';
  }

  /**
   * @returns {void}
   */
  bindPackSummaryListeners() {
    this.addEventListener('change', event => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.matches(PACK_RADIO_SELECTOR)
      ) {
        this.syncPackSummary();
      }
    });
  }

  /**
   * Disable the CTA button
   * @returns {void}
   */
  disableButton() {
    this.button.disabled = true;
  }

  /**
   * Enable the CTA button
   * @returns {void}
   */
  enableButton() {
    this.button.removeAttribute('disabled');
  }

  /**
   * Bind Add to cart: uses checked pack radio value as quantity
   * @returns {void}
   */
  bindEventListeners() {
    this.button.addEventListener('click', async event => {
      event.preventDefault();
      event.stopPropagation();

      const radio = this.querySelector(`${PACK_RADIO_SELECTOR}:checked`);
      if (!radio) {
        return;
      }

      const form = this.closest('form');
      if (!form) {
        return;
      }

      const variantId = getVariantIdFromProductForm(form);
      const packSize = Number.parseInt(radio.value, 10);

      if (!variantId || !Number.isFinite(packSize) || packSize <= 0) {
        return;
      }

      const errorContent = this.querySelector('[data-error-message]');
      await addItemToCart(
        variantId,
        packSize,
        this.button,
        errorContent,
        this.tagName.toLowerCase()
      );
    });
  }

  /**
   * Returns all the product-multi-add elements as a node list
   * @returns {NodeListOf<ProductMultiAdd>}
   */
  static getAllInstances() {
    return document.querySelectorAll('product-multi-add');
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {void}
   */
  static disableInForm(form) {
    for (const /** @type {ProductMultiAdd} */ element of form.querySelectorAll(
      'product-multi-add'
    )) {
      for (const input of element.querySelectorAll(PACK_RADIO_SELECTOR)) {
        input.disabled = true;
      }
      if (element.button) {
        element.disableButton();
      }
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {void}
   */
  static enableInForm(form) {
    for (const /** @type {ProductMultiAdd} */ element of form.querySelectorAll(
      'product-multi-add'
    )) {
      for (const input of element.querySelectorAll(PACK_RADIO_SELECTOR)) {
        input.removeAttribute('disabled');
      }
      if (element.button) {
        element.enableButton();
      }
    }
  }

  /**
   * Disables pack radios and the bulk CTA
   * @returns {void}
   */
  static disableAll() {
    for (const /** @type {ProductMultiAdd} */ element of ProductMultiAdd.getAllInstances()) {
      for (const input of element.querySelectorAll(PACK_RADIO_SELECTOR)) {
        input.disabled = true;
      }
      if (element.button) {
        element.disableButton();
      }
    }
  }

  /**
   * Enables pack radios and the bulk CTA
   * @returns {void}
   */
  static enableAll() {
    for (const /** @type {ProductMultiAdd} */ element of ProductMultiAdd.getAllInstances()) {
      for (const input of element.querySelectorAll(PACK_RADIO_SELECTOR)) {
        input.removeAttribute('disabled');
      }
      if (element.button) {
        element.enableButton();
      }
    }
  }
}

customElements.get('product-multi-add') ||
  customElements.define('product-multi-add', ProductMultiAdd);

export default ProductMultiAdd;
